import { Button, Card, Checkbox, Col, Divider, Flex, Form, FormProps, message, Radio, RadioChangeEvent, Row } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// components
import FileInput from 'components/fileInput/fileInput';
import FormElement from 'components/formElement/formElement';
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import { UpdateDocumentRequestType } from '../models/updateDocument';
import { onGetUpdateDocumentsRequestTypes, onSubmitUpdateDocumentRequest } from '../services/updateDocumentsServices';
import NotificationService from 'core/services/notification';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { isRTL } from 'core/services/language';
import { checkArabicValidation } from 'utils/validators/arabic';
import { checkEnglishValidation } from 'utils/validators/english';
import { mapResponseErrors } from 'utils/mapResponseErrors';

// services

// styles

// models

interface UpdateDocumentCreatePageProps {}

type FieldTypes = {
	document_type: string;
	publish_at_platforms: 0 | 1;
	'new_translatable_value[ar]': string;
	'new_translatable_value[en]': string;
	new_file: any;
	new_value: string;
	signature: any;
};

const UpdateDocumentCreatePage: FunctionComponent<UpdateDocumentCreatePageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [requestForm] = Form.useForm();

	const selectedType = Form.useWatch('document_type', requestForm);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const [requestTypes, setRequestTypes] = useState<UpdateDocumentRequestType[]>([]);

	useEffect(() => {
		getDocumentUpdateRequestTypes();
	}, []);

	const validateArabic = (_: any, value: string) => {
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	const validateEnglish = (_: any, value: string) => {
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const getDocumentUpdateRequestTypes = async () => {
		try {
			setIsLoading(true);

			const availableRequestTypes = await onGetUpdateDocumentsRequestTypes();
			setRequestTypes(availableRequestTypes);

			availableRequestTypes.some((type) => {
				if (!type.has_pending_update_document) {
					requestForm.setFieldsValue({ document_type: type.key });
					return true;
				}
			});

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/update-documents');
		}
	};

	const onSubmitRequest: FormProps<FieldTypes>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createPayload = new FormData();

			Object.entries(values).forEach(([key, value]) => {
				if (key === 'publish_at_platforms') {
					createPayload.append(key, value ? '1' : '0');
					return;
				}
				createPayload.append(key, value);
			});

			await onSubmitUpdateDocumentRequest(createPayload);

			navigate('/dashboard/actions/update-documents');
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.UPDATE_DOCUMENT_SUCCESS'));
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
			mapResponseErrors(error, requestForm);
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			<Card>
				<ComponentHeader
					title={t('ACTIONS.UPDATE_COMPANY_DOCUMENT')}
					subtitle={t('ACTIONS.FILL_THESE_DETAILS_TO_UPDATE_COMPANY_DOCUMENTS')}
				/>
				{isLoading ? (
					<LoadingComponent />
				) : (
					<Form form={requestForm} onFinish={onSubmitRequest}>
						<h6 className="mt-4">{t('ACTIONS.DOCUMENT_TYPE')}</h6>
						<Form.Item name="document_type">
							<Radio.Group
								value={selectedType}
								style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}
								options={requestTypes.map((type) => ({
									label: (
										<span>
											{isRTL() ? type.translated_name.ar : type.translated_name.en}
											{type.has_pending_update_document && <span style={{ color: 'orange' }}> - {t('ACTIONS.HAS_PENDING_REQUEST')}</span>}
										</span>
									),
									value: type.key,
									disabled: type.has_pending_update_document,
								}))}
								onChange={() => requestForm.setFieldValue('new_file', null)}
							/>
						</Form.Item>

						<Divider />

						{requestTypes.find((type) => type.key === selectedType)?.has_integer_input && (
							<div className="mb-4">
								<FormElement
									fullWidth
									name="new_value"
									type="number"
									label={t('ACTIONS.TAX_REGISTRATION_NUMBER')}
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{ len: 15, message: t('ACTIONS.TAX_REGISTRATION_NUMBER_LEN') },
									]}
								/>
							</div>
						)}

						{requestTypes.find((type) => type.key === selectedType)?.has_translatable && (
							<Row gutter={[16, 16]}>
								<Col span={24} md={12}>
									<FormElement
										fullWidth
										name="new_translatable_value[ar]"
										type="text"
										label={t('ACTIONS.CHAIRMAN_NAME_AR')}
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}
									/>
								</Col>

								<Col span={24} md={12}>
									<FormElement
										fullWidth
										name="new_translatable_value[en]"
										type="text"
										label={t('ACTIONS.CHAIRMAN_NAME_EN')}
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}
									/>
								</Col>
							</Row>
						)}

						<div className="mt-3">
							<Form.Item name="new_file" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
								<FileInput
									onSelect={(file) => {
										requestForm.setFieldsValue({ new_file: file });
									}}
									accept="pdf"
									name={
										(isRTL()
											? requestTypes.find((type) => type.key === selectedType)?.translated_name?.ar
											: requestTypes.find((type) => type.key === selectedType)?.translated_name?.en) || ''
									}
								/>
							</Form.Item>
						</div>

						{requestTypes.find((type) => type.key === selectedType)?.has_signature && (
							<div className="mt-4">
								<Form.Item name="signature" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
									<FileInput
										onSelect={(file) => {
											requestForm.setFieldsValue({ signature: file });
										}}
										accept="photo"
										name={t(`ACTIONS.CHAIRMAN_SIGNATURE`)}
									/>
								</Form.Item>
								<p className="text-light fw-medium">{t('ACTIONS.CHAIRMAN_SIGNATURE_DESC')}</p>
							</div>
						)}

						{requestTypes.find((type) => type.key === selectedType)?.show_publish_at_platforms_checkbox && (
							<Form.Item name="publish_at_platforms">
								<Checkbox
									className="mt-4"
									defaultChecked={false}
									onChange={(event) => {
										requestForm.setFieldsValue({ publish_at_platforms: event.target.checked });
									}}>
									{t('ACTIONS.SHOW_ON_PLATFORM_CONNECTED_TO_HEFTH')}
								</Checkbox>
							</Form.Item>
						)}

						<Flex justify="flex-end" className="mt-5">
							<Button size="middle" type="primary" htmlType="submit" loading={isSubmitting}>
								{t('ACTIONS.SEND_FOR_REVIEW')}
							</Button>
						</Flex>
					</Form>
				)}
			</Card>
		</>
	);
};

export default UpdateDocumentCreatePage;
